import dayjs from "dayjs";
import type { CalendarUrlMode } from "./urlState";

export type CalendarWindowMode = "day" | "week" | "month";

export type CalendarWindowBounds = {
  windowMode: CalendarWindowMode;
  startAtIso: string;
  endAtIso: string;
};

/** Compute the inclusive fetch window for a calendar view. */
export const getCalendarWindowBounds = (
  mode: CalendarUrlMode,
  date: Date,
  startDate: Date,
): CalendarWindowBounds => {
  const windowMode: CalendarWindowMode =
    mode === "week" ? "week" : mode === "month" ? "month" : "day";

  if (windowMode === "day") {
    const start = dayjs(date).startOf("day");
    return {
      windowMode,
      startAtIso: start.toISOString(),
      endAtIso: start.endOf("day").toISOString(),
    };
  }

  if (windowMode === "week") {
    const start = dayjs(startDate).startOf("day");
    return {
      windowMode,
      startAtIso: start.toISOString(),
      endAtIso: start.add(6, "day").endOf("day").toISOString(),
    };
  }

  // Match the Monday-first 6-week month matrix painted by BigCalendar so
  // leading/trailing overflow days are fetched (e.g. 27 Jul–6 Sep for Aug 2026).
  const firstOfMonth = dayjs(date).startOf("month").startOf("day");
  const weekday = firstOfMonth.day();
  const mondayOffset = weekday === 0 ? 6 : weekday - 1;
  const gridStart = firstOfMonth.subtract(mondayOffset, "day");
  const gridEnd = gridStart.add(6 * 7 - 1, "day").endOf("day");
  return {
    windowMode,
    startAtIso: gridStart.toISOString(),
    endAtIso: gridEnd.toISOString(),
  };
};

export const buildCalendarWindowSignature = (input: {
  bounds: CalendarWindowBounds;
  sessionStartHandle: string;
  sessionEndHandle: string;
  recurrenceHandle?: string;
  seriesIdHandle?: string;
  originalStartHandle?: string;
  filtersKey: string;
  weekdaysKey?: string;
  timezone?: string;
  visibleCalendarKey?: string;
}): string => {
  const {
    bounds,
    sessionStartHandle,
    sessionEndHandle,
    recurrenceHandle = "",
    seriesIdHandle = "",
    originalStartHandle = "",
    filtersKey,
    weekdaysKey = "all",
    timezone = "",
    visibleCalendarKey = "",
  } = input;
  return [
    bounds.windowMode,
    bounds.startAtIso,
    bounds.endAtIso,
    sessionStartHandle,
    sessionEndHandle,
    recurrenceHandle,
    seriesIdHandle,
    originalStartHandle,
    filtersKey,
    weekdaysKey,
    timezone,
    visibleCalendarKey,
  ].join(":");
};

type CalendarFetchGenerationState = {
  __CALENDAR_FETCH_GENERATION__?: Record<string, number>;
};

export const nextCalendarFetchGeneration = (blockId: string): number => {
  const globalObj = globalThis as CalendarFetchGenerationState;
  if (!globalObj.__CALENDAR_FETCH_GENERATION__) {
    globalObj.__CALENDAR_FETCH_GENERATION__ = {};
  }
  const next = (globalObj.__CALENDAR_FETCH_GENERATION__[blockId] ?? 0) + 1;
  globalObj.__CALENDAR_FETCH_GENERATION__[blockId] = next;
  return next;
};

export const getLatestCalendarFetchGeneration = (blockId: string): number => {
  const globalObj = globalThis as CalendarFetchGenerationState;
  return globalObj.__CALENDAR_FETCH_GENERATION__?.[blockId] ?? 0;
};

export const markCalendarWindowLoaded = (
  blockId: string,
  windowKey: string,
): void => {
  const globalObj = globalThis as CalendarFetchGenerationState & {
    __CALENDAR_WINDOW_SIGNATURES__?: Record<string, string>;
  };
  if (!globalObj.__CALENDAR_WINDOW_SIGNATURES__) {
    globalObj.__CALENDAR_WINDOW_SIGNATURES__ = {};
  }
  globalObj.__CALENDAR_WINDOW_SIGNATURES__[blockId] = windowKey;
};

export const getLoadedCalendarWindowSignature = (
  blockId: string,
): string | undefined => {
  const globalObj = globalThis as CalendarFetchGenerationState & {
    __CALENDAR_WINDOW_SIGNATURES__?: Record<string, string>;
  };
  return globalObj.__CALENDAR_WINDOW_SIGNATURES__?.[blockId];
};

export const clearLoadedCalendarWindowSignature = (blockId: string): void => {
  const globalObj = globalThis as CalendarFetchGenerationState & {
    __CALENDAR_WINDOW_SIGNATURES__?: Record<string, string>;
  };
  if (!globalObj.__CALENDAR_WINDOW_SIGNATURES__) return;
  delete globalObj.__CALENDAR_WINDOW_SIGNATURES__[blockId];
};
